/* eslint no-use-before-define: 0, no-nested-ternary: 0 */
const _ = require('lodash');

const {
  minutesToTimeRanges,
  timeRangesToMinutes,
  parseDate,
  serializeDate,
  newScheduleDates
} = require('../utils/dates');

const PARSE_ENDDATE_NULL = '9999-01-01'; // if endDate is this string, parse it to null

const ALL_WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];
const ALL_TIMES = ['00:00', '00:00'];
const ALL_TIMES_ALTERNATE = ['00:00', '24:00'];
const TYPE_NAMES = {
  discoverable: 'discoverable',
  visible: 'visible',
  usable: 'usable'
};

const getAllDatesStart = () => serializeDate(new Date());

const getAllDatesEnd = (startDate
/*: string*/
) => {
  const today = startDate ? parseDate(startDate) : new Date();
  today.setFullYear(today.getFullYear() + 5);
  return serializeDate(today);
};

const isAllTimes = (timeRanges
/*: ?Array<[string, string]>*/
) =>
/*: boolean*/
{
  if (!timeRanges) return false;
  return !!timeRanges.find(range => {
    if (range[0] !== ALL_TIMES[0]) return false;
    return range[1] === ALL_TIMES[1] || range[1] === ALL_TIMES_ALTERNATE[1];
  });
};

const isAllWeekDays = (weekDays
/*: ?Array<number>*/
) =>
/*: boolean*/
!_.difference(ALL_WEEKDAYS, weekDays).length;

const isAllDates = (startDate
/*: string*/
, endDate
/*: string*/
) =>
/*: boolean*/
{
  // FIXME: For now this will mean "5 years" (max schedule length)
  // When we can, this should be `allDates || endDate === null`
  if (!startDate) return false;
  if (!endDate || endDate === PARSE_ENDDATE_NULL) return false;
  const expected = getAllDatesEnd(startDate);
  return expected === endDate;
};

const isEvergreen = (endDate
/*: string*/
) =>
/*: boolean*/
!endDate || endDate === PARSE_ENDDATE_NULL; // "Advanced" schedules imply custom time-of-day, day-of-week, or end-date


const isAdvanced = (schedule
/*: Schedule*/
) =>
/*: boolean*/
{
  if (!schedule.allTimes) return true;
  if (!schedule.allWeekDays) return true;
  if (!schedule.evergreen) return true;
  return false;
};

class Schedule {
  /*:: static fromJson: (Object) => Schedule */

  /*:: static serialize: (Schedule) => Object */

  /*:: static areEqual: (Schedule, Schedule) => boolean */

  /*:: static TYPE_NAMES: {[string]: string} */

  /*:: static ALL_TIMES: Array<string> */

  /*:: static ALL_WEEKDAYS: Array<number> */
  constructor({
    /*: {
        type?: ?string,
        startDate?: ?string,
        endDate?: ?string,
        weekDays?: ?Array<number>,
        timeRanges?: ?Array<[string, string]>,
        id?: ?string,
        disabled?: ?boolean,
        allWeekDays?: ?boolean,
        allTimes?: ?boolean,
        allDates?: boolean,
        partialDiscoverableSchedule?: ?boolean,
        isHidden?: boolean,
        evergreen?: boolean,
        advanced?: boolean
      }*/

    type,
    startDate,
    endDate,
    weekDays,
    timeRanges,
    id,
    disabled,
    allWeekDays,
    allTimes,
    allDates,
    partialDiscoverableSchedule,
    isHidden,
    evergreen,
    advanced
  } = {}) {
    this.type = type;
    this.startDate = startDate;
    this.endDate = endDate;
    this.weekDays = weekDays;
    this.timeRanges = timeRanges;
    this.id = id;
    this.disabled = disabled;
    this.partialDiscoverableSchedule = partialDiscoverableSchedule;
    this.isHidden = isHidden; // allTimes and allWeekdays parameters take precedence over the timeRanges or weekDays values
    // This is important when constructing a Schedule from form data

    this.allTimes = allTimes || isAllTimes(this.timeRanges);
    this.allWeekDays = allWeekDays || isAllWeekDays(this.weekDays);
    this.allDates = allDates || isAllDates(this.startDate, this.endDate);
    this.evergreen = evergreen || isEvergreen(this.endDate);
    this.advanced = advanced || isAdvanced(this);
  }

  copy(mutator
  /*: (Schedule) => void*/
  = () => undefined)
  /*: Schedule*/
  {
    // $FlowFixMe -- Flow thinks this.allTimes and this.allWeekdays will be null or undefined
    const duplicate = new Schedule(this);
    mutator(duplicate);
    return duplicate;
  }

  copyToDiscoverable() {
    return this.copy(s => {
      /* eslint-disable no-param-reassign */
      s.type = TYPE_NAMES.discoverable;

      if (!this.partialDiscoverableSchedule) {
        s.weekDays = ALL_WEEKDAYS.slice(); // $FlowFixMe

        s.timeRanges = [ALL_TIMES.slice()];
      }

      s.id = undefined;
      /* eslint-enable no-param-reassign */
    });
  }

  copyToVisible() {
    const {
      yearFromCurrentDate
    } = newScheduleDates();
    return this.copy(s => {
      /* eslint-disable no-param-reassign */
      s.type = TYPE_NAMES.visible;
      s.weekDays = ALL_WEEKDAYS.slice(); // $FlowFixMe

      s.timeRanges = [ALL_TIMES.slice()];
      s.id = undefined;

      if (!s.endDate || s.endDate === PARSE_ENDDATE_NULL) {
        s.endDate = serializeDate(yearFromCurrentDate);
      } else {
        // Keep expired offer visible for 1 week
        const visibleEnd = parseDate(s.endDate);
        visibleEnd.setDate(visibleEnd.getDate() + 7);
        s.endDate = serializeDate(visibleEnd);
      }
      /* eslint-enable no-param-reassign */

    });
  }

}

const duplicate = (refSchedules, allowAdvanced = true) => {
  const {
    currentDate,
    yearFromCurrentDate
  } = newScheduleDates();
  return refSchedules.map(s => s.copy(newSchedule => {
    /* eslint-disable no-param-reassign */
    newSchedule.id = null;
    newSchedule.startDate = serializeDate(currentDate); // duplicated offers should be evergreen by default

    newSchedule.endDate = serializeDate(yearFromCurrentDate);
    newSchedule.evergreen = true;

    if (!allowAdvanced) {
      // free plans should have the advanced options stripped out
      newSchedule.allWeekDays = true;
      newSchedule.weekDays = ALL_WEEKDAYS;
      newSchedule.allTimes = true;
      newSchedule.timeRanges = ALL_TIMES;
      newSchedule.advanced = false;
    }

    return newSchedule;
    /* eslint-enable no-param-reassign */
  }));
};

const fromJson = json => {
  const timeRanges = minutesToTimeRanges(json.minutes);
  const startDate = json.startDate ? json.startDate.split('T')[0] : null;
  const endDate = json.endDate ? json.endDate.split('T')[0] : null;
  return new Schedule({
    type: json.type,
    startDate,
    endDate,
    weekDays: json.weekDays,
    timeRanges,
    id: json.id,
    disabled: json.disabled
  });
};

const serialize = schedule => {
  const startDate = schedule.allDates ? getAllDatesStart() : schedule.startDate;
  const endDate = schedule.allDates ? getAllDatesEnd() : !schedule.advanced || schedule.evergreen ? null : schedule.endDate;
  const timeRanges = !schedule.advanced || schedule.allTimes ? [ALL_TIMES] : schedule.timeRanges;
  const minutes = timeRangesToMinutes(timeRanges || []);
  const weekDays = !schedule.advanced || schedule.allWeekDays ? ALL_WEEKDAYS : schedule.weekDays;
  return {
    type: schedule.type,
    startDate,
    endDate,
    weekDays,
    minutes
  };
};

const areEqual = (a, b) => a.type === b.type && _.isEqual(a.startDate, b.startDate) && _.isEqual(a.endDate, b.endDate) && _.isEqual(a.weekDays, b.weekDays) && _.isEqual(a.timeRanges, b.timeRanges);

Schedule.fromJson = fromJson;
Schedule.serialize = serialize;
Schedule.areEqual = areEqual;
Schedule.duplicate = duplicate;
Schedule.TYPE_NAMES = TYPE_NAMES;
Schedule.ALL_WEEKDAYS = ALL_WEEKDAYS;
Schedule.ALL_TIMES = ALL_TIMES;
module.exports = Schedule;